import React from 'react';
import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { FaAward, FaHeartbeat, FaTooth, FaSmile, FaShieldAlt } from 'react-icons/fa';

const About = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.3 });
  const [isMobile, setIsMobile] = React.useState(window.innerWidth <= 768);

  React.useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const features = [
    { 
      icon: <FaAward />, 
      title: 'Gold Medalist', 
      desc: 'Distinguished dental surgeon with academic excellence',
      color: '#D4AF37'
    },
    { 
      icon: <FaHeartbeat />, 
      title: 'Patient-Centered', 
      desc: 'Your comfort and care are our top priorities',
      color: '#C8A97E'
    },
    { 
      icon: <FaTooth />, 
      title: 'Modern Techniques', 
      desc: 'Latest dental technology for precise treatments',
      color: '#B48C5C'
    },
    { 
      icon: <FaSmile />, 
      title: 'Transform Smiles', 
      desc: 'Creating beautiful, confident smiles daily',
      color: '#D4AF37'
    }
  ];

  const achievements = [
    
    { number: '100%', label: 'Satisfaction', icon: <FaShieldAlt /> }
  ];

  const styles = {
    about: {
      padding: isMobile ? '60px 0' : '100px 0',
      background: 'linear-gradient(135deg, #151E2F, #0A0F1C)',
      position: 'relative',
      overflow: 'hidden'
    },
    container: {
      maxWidth: '1200px',
      margin: '0 auto',
      padding: isMobile ? '0 20px' : '0 40px',
      position: 'relative',
      zIndex: 2
    },
    sectionHeader: {
      textAlign: 'center',
      marginBottom: isMobile ? '30px' : '50px'
    },
    subtitle: {
      color: '#C8A97E',
      fontSize: isMobile ? '14px' : '16px',
      textTransform: 'uppercase',
      letterSpacing: isMobile ? '3px' : '4px',
      marginBottom: '10px',
      display: 'block',
      fontWeight: 600
    },
    h2: {
      fontSize: isMobile ? 'clamp(28px, 6vw, 36px)' : 'clamp(36px, 5vw, 48px)',
      color: '#ffffff',
      marginBottom: '15px',
      fontFamily: 'Playfair Display, serif',
      fontWeight: 800
    },
    titleGradient: {
      background: 'linear-gradient(135deg, #C8A97E, #D4AF37)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent'
    },
    divider: {
      width: isMobile ? '60px' : '80px',
      height: '3px',
      background: 'linear-gradient(135deg, #C8A97E, #D4AF37)',
      margin: '0 auto',
      borderRadius: '2px'
    },
    content: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
      gap: isMobile ? '30px' : '50px',
      alignItems: 'center'
    },
    leftContent: {
      position: 'relative'
    },
    imageGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, 1fr)',
      gap: isMobile ? '10px' : '15px'
    },
    imageCard: {
      position: 'relative',
      borderRadius: isMobile ? '12px' : '15px',
      overflow: 'hidden',
      aspectRatio: '1/1',
      boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)'
    },
    image1: {
      width: '100%',
      height: '100%',
      background: 'url("https://images.unsplash.com/photo-1629909613654-28e377c37b09?ixlib=rb-4.0.3") center/cover',
      transition: 'transform 0.5s'
    },
    image2: {
      width: '100%',
      height: '100%',
      background: 'url("https://images.unsplash.com/photo-1609840114035-3c981b782dfe?ixlib=rb-4.0.3") center/cover',
      transition: 'transform 0.5s'
    },
    image3: {
      width: '100%',
      height: '100%',
      background: 'url("https://images.unsplash.com/photo-1588776814546-1ffcf47267a5?ixlib=rb-4.0.3") center/cover',
      transition: 'transform 0.5s'
    },
    image4: {
      width: '100%',
      height: '100%',
      background: 'url("https://images.unsplash.com/photo-1622253692010-333f2da6031d?ixlib=rb-4.0.3") center/cover',
      transition: 'transform 0.5s'
    },
    experienceBadge: {
      position: 'absolute',
      bottom: isMobile ? '-10px' : '-15px',
      right: isMobile ? '-10px' : '-15px',
      background: 'linear-gradient(135deg, #C8A97E, #D4AF37)',
      padding: isMobile ? '20px' : '25px',
      borderRadius: '50%',
      width: isMobile ? '100px' : '120px',
      height: isMobile ? '100px' : '120px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      boxShadow: '0 15px 30px rgba(0, 0, 0, 0.3)',
      zIndex: 3
    },
    expNumber: {
      fontSize: isMobile ? '28px' : '36px',
      fontWeight: 900,
      color: '#0A0F1C',
      lineHeight: 1
    },
    expText: {
      fontSize: isMobile ? '10px' : '12px',
      color: '#0A0F1C',
      textAlign: 'center',
      fontWeight: 700,
      textTransform: 'uppercase'
    },
    rightContent: {
      color: '#ffffff'
    },
    aboutText: {
      marginBottom: isMobile ? '25px' : '35px'
    },
    aboutTitle: {
      fontSize: isMobile ? '24px' : '32px',
      color: '#ffffff',
      marginBottom: '15px',
      fontFamily: 'Playfair Display, serif',
      fontWeight: 700,
      textAlign: isMobile ? 'center' : 'left'
    },
    aboutDesc: {
      color: '#A0A8B8',
      lineHeight: 1.8,
      marginBottom: '15px',
      fontSize: isMobile ? '14px' : '15px',
      textAlign: isMobile ? 'center' : 'left'
    },
    featuresGrid: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
      gap: isMobile ? '15px' : '20px',
      marginBottom: isMobile ? '25px' : '30px'
    },
    featureCard: {
      background: 'rgba(255, 255, 255, 0.03)',
      padding: isMobile ? '20px' : '25px',
      borderRadius: '12px',
      border: '1px solid rgba(200, 169, 126, 0.15)',
      backdropFilter: 'blur(10px)',
      transition: 'all 0.3s',
      textAlign: isMobile ? 'center' : 'left'
    },
    featureIcon: {
      fontSize: isMobile ? '28px' : '32px',
      marginBottom: '10px'
    },
    featureTitle: {
      fontSize: isMobile ? '18px' : '20px',
      color: '#ffffff',
      marginBottom: '8px',
      fontFamily: 'Playfair Display, serif',
      fontWeight: 600
    },
    featureDesc: {
      fontSize: isMobile ? '13px' : '14px',
      color: '#A0A8B8',
      lineHeight: 1.6
    },
    achievements: {
      display: 'grid',
      gridTemplateColumns: isMobile ? 'repeat(3, 1fr)' : 'repeat(3, 1fr)',
      gap: isMobile ? '10px' : '20px',
      background: 'rgba(255, 255, 255, 0.02)',
      padding: isMobile ? '15px' : '20px',
      borderRadius: '15px',
      border: '1px solid rgba(200, 169, 126, 0.15)'
    },
    achievementItem: {
      textAlign: 'center'
    },
    achievementIcon: {
      fontSize: isMobile ? '24px' : '30px',
      color: '#C8A97E',
      marginBottom: isMobile ? '5px' : '8px'
    },
    achievementNumber: {
      fontSize: isMobile ? '18px' : '24px',
      fontWeight: 800,
      color: '#ffffff',
      display: 'block',
      marginBottom: '3px'
    },
    achievementLabel: {
      fontSize: isMobile ? '10px' : '12px',
      color: '#A0A8B8',
      textTransform: 'uppercase',
      letterSpacing: '1px',
      fontWeight: 500
    }
  };

  return (
    <section id="about" style={styles.about} ref={ref}>
      <div style={styles.container}>
        <motion.div 
          style={styles.sectionHeader}
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <span style={styles.subtitle}>About Us</span>
          <h2 style={styles.h2}>
            Meet Your <span style={styles.titleGradient}>Doctor</span>
          </h2>
          <div style={styles.divider}></div>
        </motion.div>

        <div style={styles.content}>
          <motion.div 
            style={styles.leftContent}
            initial={{ opacity: 0, x: isMobile ? 0 : -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <div style={styles.imageGrid}>
              {[1, 2, 3, 4].map((item, index) => (
                <motion.div 
                  key={index}
                  style={styles.imageCard}
                  whileHover={{ scale: 1.05 }}
                  transition={{ duration: 0.3 }}
                >
                  <div style={styles[`image${item}`]}></div>
                </motion.div>
              ))}
            </div>
            
            
          </motion.div>

          <motion.div 
            style={styles.rightContent}
            initial={{ opacity: 0, x: isMobile ? 0 : 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <div style={styles.aboutText}>
              <h3 style={styles.aboutTitle}>A Commitment to Excellence</h3>
              <p style={styles.aboutDesc}>
                Dr. Muhammad Athar Munawar is a distinguished Gold Medalist Dental Surgeon 
                serving the community of Multan with years of experience.
              </p>
              <p style={styles.aboutDesc}>
                At Maxfax Dental Clinic, we believe in patient-centered care using the 
                latest dental technology for accurate diagnoses and comfortable treatments.
              </p>
            </div>

            <div style={styles.featuresGrid}>
              {features.map((feature, index) => (
                <motion.div 
                  key={index}
                  style={styles.featureCard}
                  initial={{ opacity: 0, y: 20 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ delay: 0.5 + index * 0.1 }}
                  whileHover={{ 
                    scale: 1.03,
                    borderColor: feature.color
                  }}
                >
                  <div style={{ ...styles.featureIcon, color: feature.color }}>
                    {feature.icon}
                  </div>
                  <h4 style={styles.featureTitle}>{feature.title}</h4>
                  <p style={styles.featureDesc}>{feature.desc}</p>
                </motion.div>
              ))}
            </div>

            <div style={styles.achievements}>
              {achievements.map((item, index) => (
                <motion.div 
                  key={index}
                  style={styles.achievementItem}
                  initial={{ opacity: 0, y: 20 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ delay: 0.8 + index * 0.1 }}
                >
                  <div style={styles.achievementIcon}>{item.icon}</div>
                  <span style={styles.achievementNumber}>{item.number}</span>
                  <span style={styles.achievementLabel}>{item.label}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default About;